//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useConfig } from './Config';
import { mapProductPathToSolutionRoute } from './utils/validateUrlUtils';
import { useUserAuth } from './UserAuth';
import Loading from './components/loading/loading';
import NoAccessPage from './components/Home/NoAccessPage';
import NoLicensePage from './components/Loops/NoLicensePage';
import NOACCESS from './icons/NoAccess.svg';

type ProtectedRouteProps = {
  element: React.ReactNode;
  requiresAdmin?: boolean;
};

const getLicenseMessageKey = (code: number | undefined): string => {
  if (code === undefined || code === null) return 'no-license-message';
  switch (code) {
    case 402:
      return 'NoAccessLicense';
    case 404:
      return 'license-unassigned-tier';
    case 503:
      return 'license-server-connection-failed';
    default:
      if (code >= 400 && code < 600) return 'license-generic-error';
      return 'no-license-message';
  }
};

const ProtectedRoute = ({
  element,
  requiresAdmin,
}: Readonly<ProtectedRouteProps>) => {
  const {
    isAuthenticating,
    isAuthenticated,
    hasBasicRole,
    isLoading,
    licenses,
    licenseErrorCode,
    roles,
  } = useUserAuth();
  const { baseName, solutions } = useConfig();
  const navigate = useNavigate();
  const isAdmin = roles?.includes('ROLE_ADMIN');

  if (!isAuthenticated && isAuthenticating) {
    //If we are in the middle of authenticating, but not refreshing a token
    return <Loading />;
  }

  if (isAuthenticated && isLoading) {
    //If we have authenticated, but are still loading user data
    return <Loading />;
  }

  if (isAuthenticated && !hasBasicRole) {
    //If we have authenticated, but we do not have a basic role
    return (
      <NoLicensePage
        onClose={() => navigate('/logout')}
        messageKey="no-role-message"
      />
    );
  }

  if (isAuthenticated && hasBasicRole && licenses?.length === 0) {
    //If we have authenticated with a basic role, but we do not have any license
    return (
      <NoLicensePage
        onClose={() => navigate('/logout')}
        messageKey={getLicenseMessageKey(licenseErrorCode)}
      />
    );
  }

  if (isAuthenticated && hasBasicRole && requiresAdmin && !isAdmin) {
    return <NoAccessPage message="No admin access" icon={NOACCESS} />;
  }

  if (isAuthenticated && hasBasicRole) {
    //hasBasicRole is somewhat redundant here, but important if the
    //no-access behavior changes
    return <>{element}</>;
  }

  //If not authenticated, redirect to login
  // SwitcherIframe syncs the browser URL to the iframe's internal URL via
  // history.replaceState (e.g. /plan/#/ccmweb/welcome). That uses the product
  // ingress path, not the React Router route (/solution/plan). Map it back so
  // the post-login redirect resolves to a valid route.
  const rawLocation = `${window.location.pathname.replace(baseName, '')}${window.location.search}${window.location.hash}`;
  const redirectLocation = mapProductPathToSolutionRoute(
    rawLocation,
    solutions,
  );
  return (
    <Navigate
      to={`/login?redirectLocation=${encodeURIComponent(redirectLocation)}`}
    />
  );
};

export default ProtectedRoute;
